import { useMemo } from "react";
import { useStore } from "@/app/store";
import type {
  AddCostFormValues,
  FormulaVariable,
  VariableInputType,
} from "../components/CostPanel/CostPanel.types";
import type { CalculationCategoryResult } from "./useCalculationDetail";
import { derivePlanningMonths, pickActiveBucket } from "./addCostPanel.helpers";
import { expressionToTokens } from "../formula/expressionToTokens";
import useOpDataCatalog, {
  type OpDataVariableOption,
} from "./useOpDataCatalog";

type VariableInput = CalculationCategoryResult["variableInputs"][number];

const buildDefaultFormValues = (
  formulaVariables: FormulaVariable[],
  planningMonths: { key: string }[],
  variableInputs: VariableInput[],
  activeDimensionValue: string | null,
): AddCostFormValues => {
  // Seed all variable fields with empty values so reset() always clears
  // fields that have no server data (e.g. a newly added dimension value).
  const result: AddCostFormValues = {};
  for (const variable of formulaVariables) {
    result[variable.id] =
      variable.inputType === "PER_MONTH"
        ? Object.fromEntries(planningMonths.map((month) => [month.key, ""]))
        : "";
  }
  for (const input of variableInputs) {
    // Saved inputs can outlive the formula (e.g. a formula edit drops a
    // variable); only seed fields the current formula still has.
    const seededField = result[input.variableId];
    if (seededField === undefined) continue;
    const entry = input.valuesByDimension.find(
      (savedEntry) => savedEntry.dimensionValue === activeDimensionValue,
    );
    if (!entry) continue;
    if (
      entry.distributionType === "PER_MONTH" &&
      entry.monthValues &&
      typeof seededField === "object"
    ) {
      for (const { year, monthNumber, value } of entry.monthValues) {
        const key = `${year}-${String(monthNumber).padStart(2, "0")}`;
        seededField[key] = String(value);
      }
    } else if (entry.singleValue != null && typeof seededField !== "object") {
      result[input.variableId] = String(entry.singleValue);
    }
  }
  return result;
};

interface UseAddCostFormSeedArgs {
  activeCategory: CalculationCategoryResult | undefined;
  activeDimensionValue: string | null;
}

const useAddCostFormSeed = ({
  activeCategory,
  activeDimensionValue,
}: UseAddCostFormSeedArgs) => {
  const startDate = useStore((s) => s.calculation?.startDate ?? "");
  const endDate = useStore((s) => s.calculation?.endDate ?? "");
  const line = useStore((s) => s.calculation?.line ?? null);
  const { opDataVariables } = useOpDataCatalog();

  const planningMonths = useMemo(
    () =>
      startDate && endDate ? derivePlanningMonths(startDate, endDate) : [],
    [startDate, endDate],
  );

  const activeBucket = useMemo(
    () =>
      activeCategory
        ? pickActiveBucket(activeCategory, activeDimensionValue)
        : null,
    [activeCategory, activeDimensionValue],
  );

  const formulaVariables: FormulaVariable[] = useMemo(() => {
    if (!activeCategory || !activeBucket) return [];
    const neededIds = new Set(activeBucket.formula.extractedVariableIds ?? []);
    return activeCategory.referencedVariableSnapshots
      .filter((snap) => neededIds.has(snap.variableId))
      .map((snap) => ({
        id: snap.variableId,
        name: snap.displayName,
        inputType: snap.defaultDistributionType as VariableInputType,
        unit: snap.unit,
      }));
  }, [activeCategory, activeBucket]);

  const formulaExpression = activeBucket?.formula.expression ?? "";
  const sourceTemplateId = activeBucket?.formula.sourceTemplateId ?? null;

  // Op Data variables aren't in the formula's snapshots, so derive them from the
  // expression ∩ the op-data catalog. Empty while the catalog is loading/errored.
  const referencedOpDataVariables = useMemo<OpDataVariableOption[]>(() => {
    const tokens = expressionToTokens(formulaExpression);
    if (!tokens) return [];
    const referencedCodes = new Set(
      tokens
        .filter((token) => token.kind === "var")
        .map((token) => token.value),
    );
    return opDataVariables.filter((sv) => referencedCodes.has(sv.code));
  }, [formulaExpression, opDataVariables]);

  // code -> displayName for the variables this category references. The formula
  // chips key by code, so this lets them render display names from already-
  // loaded snapshot data — no dependency on the async variable pool.
  const referencedVariableNames = useMemo<Record<string, string>>(() => {
    const names: Record<string, string> = {};
    for (const snap of activeCategory?.referencedVariableSnapshots ?? []) {
      names[snap.code] = snap.displayName;
    }
    return names;
  }, [activeCategory]);

  const defaultFormValues = useMemo(
    () =>
      activeCategory
        ? buildDefaultFormValues(
            formulaVariables,
            planningMonths,
            activeCategory.variableInputs,
            activeDimensionValue,
          )
        : {},
    [activeCategory, activeDimensionValue, formulaVariables, planningMonths],
  );

  return {
    planningMonths,
    formulaVariables,
    formulaExpression,
    sourceTemplateId,
    referencedVariableNames,
    referencedOpDataVariables,
    line,
    defaultFormValues,
  };
};

export default useAddCostFormSeed;
